import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Loading } from '@Components';
import { bopsApi } from '@Network';
import { authorizationOperations, clientOperations, refreshAccessToken, useAppDispatch } from '@Store';
import SmbUnauthorized from '../../SmbUnauthorized/smbUnauthorized';

type ProvisioningState = 'provisioning' | 'failed';

/**
 * Provisioning flow for GS1 SMB suppliers whose token carries a valid
 * membership + GLN but no `clientId` yet. On mount it asks the backend to
 * resolve (or create) the supplier client and bind the new `clientId` to the
 * user's Auth0 `app_metadata`, then forces a silent token refresh and refetches
 * the client configuration so `WithSidebar` re-renders into the actual app.
 *
 * On any failure (GLN not in DB nor Siebel, etc.) the user lands on the
 * SmbUnauthorized page — same terminal as the membership gate, matching the
 * "unauthorized + raise alert" leaf of the onboarding decision tree.
 */
const Gs1Provisioning = () => {
  const [state, setState] = useState<ProvisioningState>('provisioning');
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  // React 18 StrictMode runs effects twice in dev. Provisioning has external
  // side effects (DB insert, Auth0 write) that race when invoked concurrently,
  // so guard against the second invocation. Safe in production too.
  const hasStartedRef = useRef(false);

  useEffect(() => {
    if (hasStartedRef.current) return () => undefined;
    hasStartedRef.current = true;

    let cancelled = false;
    const run = async (): Promise<void> => {
      await bopsApi.provisionSupplier();
      if (cancelled) return;
      await refreshAccessToken();
      if (cancelled) return;
      await dispatch(authorizationOperations.updateTokenData());
      if (cancelled) return;
      await dispatch(clientOperations.fetchClientConfiguration());
    };
    run().catch(() => {
      if (!cancelled) setState('failed');
    });
    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  if (state === 'failed') return <SmbUnauthorized />;

  return (
    <Loading
      title={t('Gs1Provisioning.SettingUp', {
        defaultValue: 'Configurando tu espacio…',
      })}
    />
  );
};

export default Gs1Provisioning;
